import dayjs from 'dayjs';

export const DEFAULT_PROPOSAL_FILTERS = Object.freeze({
  begin_date: '',
  end_date: '',
  multi_search: '',
  external_proposal_id: '',
  status: '',
  contract_status: '',
  contract_number: '',
  promoter_id: '',
  responsible_id: '',
  issue_type: '',
  supplier_id: '',
  purchaser_document_number: '',
  antifraud_completed: false,
  external_proposal_id_not_null: false,
  self_contracting: false,
  assignee: '',
  week_day: '',
  priority_level: '',
  with_shutdown: false,
});

export const DEFAULT_PROPOSAL_FILTERS_NAMES = Object.freeze({
  range: 'Período',
  multi_search: 'Cliente',
  external_proposal_id: 'CCB',
  status: 'Status',
  contract_status: 'Status',
  contract_number: 'N° do contrato',
  promoter_id: 'Promotor',
  responsible_id: 'Responsável',
  issue_type: 'Tipos de pendência',
  supplier_id: 'Banco',
  purchaser_document_number: 'Comprador',
  antifraud_completed: 'Formalizados',
  external_proposal_id_not_null: 'CCB Gerada',
  self_contracting: 'Auto-contratação',
  assignee: 'Cessionário',
  week_day: 'Dia da Semana',
  priority_level: 'Prioridade',
  with_shutdown: 'Apenas afastados',
});

export const ISSUE_TYPE_OPTIONS = Object.freeze([
  { name: 'Financeiro', value: 'Financeiro' },
  { name: 'Formalização', value: 'Formalização' },
  { name: 'Problemas de averbação', value: 'Garantia' },
]);

const CLEAR_PROPOSAL_QUICK_FILTER = Object.freeze({
  status: '',
  issue_type: '',
  begin_date: '',
  end_date: '',
});

function today() {
  return dayjs().format('DD/MM/YYYY');
}

function monthStart() {
  return dayjs().startOf('month').format('DD/MM/YYYY');
}

export const PROPOSAL_QUICK_FILTERS = Object.freeze([
  {
    label: 'Pendência · Formalização',
    fields: ['status', 'issue_type'],
    getPatch: () => ({ ...CLEAR_PROPOSAL_QUICK_FILTER, status: 'Pendência', issue_type: 'Formalização' }),
    isActive: (filters) => filters.status === 'Pendência' && filters.issue_type === 'Formalização',
  },
  {
    label: 'Pendência · Financeiro',
    fields: ['status', 'issue_type'],
    getPatch: () => ({ ...CLEAR_PROPOSAL_QUICK_FILTER, status: 'Pendência', issue_type: 'Financeiro' }),
    isActive: (filters) => filters.status === 'Pendência' && filters.issue_type === 'Financeiro',
  },
  {
    label: 'Pendência · Averbação',
    fields: ['status', 'issue_type'],
    getPatch: () => ({ ...CLEAR_PROPOSAL_QUICK_FILTER, status: 'Pendência', issue_type: 'Garantia' }),
    isActive: (filters) => filters.status === 'Pendência' && filters.issue_type === 'Garantia',
  },
  {
    label: 'Em análise (hoje)',
    fields: ['status', 'begin_date', 'end_date'],
    getPatch: () => ({
      ...CLEAR_PROPOSAL_QUICK_FILTER,
      status: 'Em análise',
      begin_date: today(),
      end_date: today(),
    }),
    isActive: (filters) => filters.status === 'Em análise'
      && filters.begin_date === today()
      && filters.end_date === today(),
  },
  {
    label: 'Pagas no mês',
    fields: ['status', 'begin_date', 'end_date'],
    getPatch: () => ({
      ...CLEAR_PROPOSAL_QUICK_FILTER,
      status: 'Paga',
      begin_date: monthStart(),
      end_date: today(),
    }),
    isActive: (filters) => filters.status === 'Paga'
      && filters.begin_date === monthStart()
      && filters.end_date === today(),
  },
]);

export function clearProposalQuickFilter() {
  return { ...CLEAR_PROPOSAL_QUICK_FILTER };
}

// Mirrors the v-if guards on ProposalFilters.vue's own fields — a quick filter
// combo is only visible where every field it sets is itself visible there.
const QUICK_FILTER_FIELD_AVAILABILITY = {
  status: ({ isCreditor, isContractManagement }) => !isCreditor && !isContractManagement,
  issue_type: ({ isContractManagement }) => !isContractManagement,
  begin_date: () => true,
  end_date: () => true,
};

export function isProposalQuickFilterVisible(option, context) {
  return option.fields.every((field) => QUICK_FILTER_FIELD_AVAILABILITY[field](context));
}

export const PROPOSAL_TEXT_FILTERS = Object.freeze([
  'multi_search',
  'external_proposal_id',
  'contract_number',
]);

export function buildTrimmedTextFiltersPatch(filters, keys = PROPOSAL_TEXT_FILTERS) {
  if (!filters || typeof filters !== 'object') return {};

  return keys.reduce((patch, key) => {
    const value = filters[key];

    if (typeof value !== 'string') return patch;

    const trimmed = value.trim();
    if (trimmed === value) return patch;

    return { ...patch, [key]: trimmed };
  }, {});
}

export function createDefaultProposalFilters() {
  return { ...DEFAULT_PROPOSAL_FILTERS };
}

export function createDefaultProposalFiltersNames() {
  return { ...DEFAULT_PROPOSAL_FILTERS_NAMES };
}

export function buildContractManagementBackQuery(query = {}) {
  const { communication_id, ...rest } = query || {};

  return rest;
}

/**
 * Identidade da bancarizadora de um contrato.
 *
 * A listagem devolve `supplier_id` na raiz (vem de um join com proposals), mas
 * o endpoint de detalhe não — lá ele só existe dentro da simulação, que chega
 * ora como objeto, ora como string JSON. Sem isto o fluxo de proposta não
 * resolve a célula e a modal abre vazia.
 */
export function contractSupplierId(contract) {
  if (contract?.supplier_id) return contract.supplier_id;

  const raw = contract?.simulation;
  if (!raw) return null;

  if (typeof raw !== 'string') return raw.supplier_id ?? null;

  try {
    return JSON.parse(raw)?.supplier_id ?? null;
  } catch {
    return null;
  }
}
